import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * DepartmentsPage - Department Management
 *
 * Configuration page for managing organisational departments
 * and their equipment policies.
 */
import { useState, useCallback } from 'react';
import { ActionIcon, Box, Button, Group, Menu, Modal, NumberInput, Stack, Table, Text, TextInput, Tooltip, } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useDepartments, useCreateDepartment, useUpdateDepartment, useDeleteDepartment } from '@commander/api-client';
import { IconPlus, IconUpload, IconDownload, IconEdit, IconTrash } from '@/components/icons';
import { BulkImportModal } from '@/components/BulkImportModal';
import { PanelHeader } from '@/components/PanelHeader';
/* ====================================
   CSS Keyframe Animations (injected once)
   Premium animations for table rows and page entrance.
   ==================================== */
const deptsKeyframes = `
  @keyframes deptsRowSlideIn {
    from {
      opacity: 0;
      transform: translateY(6px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes deptsPageFadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;
// Inject keyframes into document head (only once)
if (typeof document !== 'undefined') {
    const styleId = 'depts-keyframes';
    if (!document.getElementById(styleId)) {
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = deptsKeyframes;
        document.head.appendChild(style);
    }
}
/** Metric display for counts - refined corporate style */
function CountMetric({ value, label }) {
    return (_jsxs(Group, { gap: 4, children: [_jsx(Text, { ff: "monospace", fw: 400, size: "sm", c: "dimmed", children: value }), _jsx(Text, { size: "xs", fw: 400, c: "dimmed", children: label })] }));
}
const DEPARTMENT_COLUMNS = ['name', 'maxCheckoutDurationHours', 'stunDelaySeconds'];
const DEPARTMENT_SAMPLE_DATA = [
    { name: 'Operations', maxcheckoutdurationhours: '8', stundelayseconds: '300' },
    { name: 'Security', maxcheckoutdurationhours: '12', stundelayseconds: '600' },
];
export function DepartmentsPage() {
    const [importModalOpen, setImportModalOpen] = useState(false);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [newDepartment, setNewDepartment] = useState({
        name: '',
        maxCheckoutDurationHours: 8,
        stunDelaySeconds: 300,
    });
    const [editTarget, setEditTarget] = useState(null);
    const [editForm, setEditForm] = useState({ name: '', maxCheckoutDurationHours: 8, stunDelaySeconds: 300 });
    const [deleteTarget, setDeleteTarget] = useState(null);
    const { data, isLoading } = useDepartments();
    const createDepartment = useCreateDepartment();
    const updateDepartment = useUpdateDepartment();
    const deleteDepartment = useDeleteDepartment();
    const handleImport = useCallback(async (_file) => {
        // Import endpoint not yet implemented
        return { imported: 0, errors: [{ row: 1, message: 'Import endpoint not yet implemented' }] };
    }, []);
    const handleCreate = useCallback(async () => {
        try {
            await createDepartment.mutateAsync(newDepartment);
            notifications.show({
                title: 'Department Created',
                message: `Successfully created department ${newDepartment.name}`,
                color: 'green',
            });
            setCreateModalOpen(false);
            setNewDepartment({ name: '', maxCheckoutDurationHours: 8, stunDelaySeconds: 300 });
        }
        catch {
            notifications.show({
                title: 'Error',
                message: 'Failed to create department',
                color: 'red',
            });
        }
    }, [createDepartment, newDepartment]);
    const openEdit = useCallback((dept) => {
        setEditForm({ name: dept.name, maxCheckoutDurationHours: dept.maxCheckoutDurationHours, stunDelaySeconds: dept.stunDelaySeconds });
        setEditTarget(dept);
    }, []);
    const handleUpdate = useCallback(async () => {
        if (!editTarget)
            return;
        try {
            await updateDepartment.mutateAsync({ id: editTarget.id, ...editForm });
            notifications.show({ title: 'Department Updated', message: `Updated ${editForm.name}`, color: 'green' });
            setEditTarget(null);
        }
        catch {
            notifications.show({ title: 'Error', message: 'Failed to update department', color: 'red' });
        }
    }, [updateDepartment, editTarget, editForm]);
    const handleDelete = useCallback(async () => {
        if (!deleteTarget)
            return;
        try {
            await deleteDepartment.mutateAsync(deleteTarget.id);
            notifications.show({ title: 'Department Deleted', message: `Deleted ${deleteTarget.name}`, color: 'green' });
            setDeleteTarget(null);
        }
        catch {
            notifications.show({ title: 'Error', message: 'Cannot delete department with assigned equipment or users', color: 'red' });
        }
    }, [deleteDepartment, deleteTarget]);
    const handleExport = useCallback(() => {
        if (!data?.items.length) {
            notifications.show({
                title: 'No Data',
                message: 'No departments to export',
                color: 'yellow',
            });
            return;
        }
        const headers = ['name', 'maxCheckoutDurationHours', 'stunDelaySeconds', 'equipmentCount', 'userCount'];
        const csv = [
            headers.join(','),
            ...data.items.map((item) => headers.map((h) => String(item[h] || '')).join(',')),
        ].join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `departments-export-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        notifications.show({
            title: 'Export Complete',
            message: `Exported ${data.items.length} department records`,
            color: 'green',
        });
    }, [data]);
    return (_jsxs(Stack, { gap: "lg", style: { animation: 'deptsPageFadeIn 400ms ease-out' }, children: [_jsxs(Box, { children: [_jsx(Text, { style: {
                            fontSize: '1.75rem',
                            fontWeight: 500,
                            letterSpacing: '-0.01em',
                            color: 'var(--text-primary)',
                        }, children: "Departments" }), _jsx(Text, { style: {
                            fontSize: '0.875rem',
                            fontWeight: 400,
                            color: 'var(--text-muted)',
                            marginTop: 4,
                        }, children: "Manage organisational units and equipment policies" })] }), _jsxs(Box, { style: {
                    backgroundColor: 'var(--bg-card)',
                    border: '1px solid var(--border-colour)',
                    borderRadius: 6,
                    overflow: 'hidden',
                }, children: [_jsx(PanelHeader, { title: "Department Registry", accentColour: "#1e293b", children: _jsxs(Group, { gap: "sm", children: [_jsxs(Menu, { shadow: "md", width: 200, children: [_jsx(Menu.Target, { children: _jsx(Button, { variant: "subtle", size: "xs", color: "gray", leftSection: _jsx(IconDownload, { size: 14 }), children: "Export" }) }), _jsx(Menu.Dropdown, { children: _jsx(Menu.Item, { onClick: handleExport, children: "Export to CSV" }) })] }), _jsx(Button, { variant: "subtle", size: "xs", color: "gray", leftSection: _jsx(IconUpload, { size: 14 }), onClick: () => setImportModalOpen(true), children: "Import" }), _jsx(Button, { variant: "light", size: "xs", leftSection: _jsx(IconPlus, { size: 14 }), color: "blue", onClick: () => setCreateModalOpen(true), children: "Add Department" })] }) }), _jsxs(Box, { style: { padding: '1rem' }, children: [_jsxs(Table, { styles: {
                                    table: { borderCollapse: 'separate', borderSpacing: 0 },
                                    th: {
                                        fontSize: '0.7rem',
                                        fontWeight: 500,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.08em',
                                        color: 'var(--text-muted)',
                                        padding: '0.75rem 1rem',
                                        borderBottom: '1px solid var(--border-colour)',
                                        background: 'var(--bg-base)',
                                    },
                                    td: {
                                        padding: '0.75rem 1rem',
                                        borderBottom: '1px solid var(--border-colour)',
                                        color: 'var(--text-primary)',
                                        transition: 'background 150ms ease',
                                    },
                                    tr: {
                                        '&:hover td': {
                                            background: 'var(--bg-base)',
                                        },
                                    },
                                }, children: [_jsx(Table.Thead, { children: _jsxs(Table.Tr, { children: [_jsx(Table.Th, { children: "Name" }), _jsx(Table.Th, { children: "Max Checkout" }), _jsx(Table.Th, { children: "Stun Delay" }), _jsx(Table.Th, { children: "Equipment" }), _jsx(Table.Th, { children: "Users" }), _jsx(Table.Th, { style: { width: 100 }, children: "Actions" })] }) }), _jsx(Table.Tbody, { children: isLoading ? (_jsx(Table.Tr, { children: _jsx(Table.Td, { colSpan: 6, children: _jsx(Text, { ta: "center", py: "xl", size: "sm", c: "dimmed", children: "Loading..." }) }) })) : data?.items.length === 0 ? (_jsx(Table.Tr, { children: _jsx(Table.Td, { colSpan: 6, children: _jsx(Text, { ta: "center", py: "xl", c: "dimmed", children: "No departments found." }) }) })) : (data?.items.map((dept, rowIndex) => (_jsxs(Table.Tr, { style: {
                                                /* Premium: staggered row entrance with fade-in and slide-up */
                                                animation: `deptsRowSlideIn 350ms ease-out ${rowIndex * 40}ms backwards`,
                                                /* Premium: left border accent that appears on hover via box-shadow inset */
                                                transition: 'box-shadow 200ms ease, background-color 150ms ease',
                                                boxShadow: 'inset 0 0 0 0 transparent',
                                            }, onMouseEnter: (e) => {
                                                e.currentTarget.style.boxShadow = 'inset 3px 0 0 0 #3B82F6';
                                            }, onMouseLeave: (e) => {
                                                e.currentTarget.style.boxShadow = 'inset 0 0 0 0 transparent';
                                            }, children: [_jsx(Table.Td, { children: _jsx(Text, { fw: 400, children: dept.name }) }), _jsx(Table.Td, { children: _jsxs(Text, { ff: "monospace", fw: 400, size: "sm", c: "dimmed", children: [dept.maxCheckoutDurationHours, "h"] }) }), _jsx(Table.Td, { children: _jsxs(Text, { ff: "monospace", fw: 400, size: "sm", c: "dimmed", children: [dept.stunDelaySeconds, "s"] }) }), _jsx(Table.Td, { children: _jsx(CountMetric, { value: dept.equipmentCount, label: "items" }) }), _jsx(Table.Td, { children: _jsx(CountMetric, { value: dept.userCount, label: "users" }) }), _jsx(Table.Td, { children: _jsxs(Group, { gap: 4, children: [_jsx(Tooltip, { label: "Edit", children: _jsx(ActionIcon, { variant: "subtle", size: "sm", color: "gray", onClick: () => openEdit(dept), children: _jsx(IconEdit, { size: 14 }) }) }), _jsx(Tooltip, { label: "Delete", children: _jsx(ActionIcon, { variant: "subtle", size: "sm", color: "red", onClick: () => setDeleteTarget(dept), children: _jsx(IconTrash, { size: 14 }) }) })] }) })] }, dept.id)))) })] }), data?.items.length ? (_jsx(Box, { style: {
                                    marginTop: '1rem',
                                    paddingTop: '0.75rem',
                                    borderTop: '1px solid var(--border-colour)',
                                }, children: _jsxs(Text, { size: "xs", c: "dimmed", ff: "monospace", children: [data.items.length, " department", data.items.length !== 1 ? 's' : '', " configured"] }) })) : null] })] }), _jsx(BulkImportModal, { opened: importModalOpen, onClose: () => setImportModalOpen(false), title: "Import Departments", entityType: "departments", expectedColumns: DEPARTMENT_COLUMNS, onImport: handleImport, sampleData: DEPARTMENT_SAMPLE_DATA }), _jsx(Modal, { opened: !!editTarget, onClose: () => setEditTarget(null), title: "Edit Department", styles: {
                    header: { backgroundColor: 'var(--bg-card)', borderBottom: '1px solid var(--border-colour)' },
                    body: { backgroundColor: 'var(--bg-base)' },
                    title: { fontWeight: 600, color: 'var(--text-primary)' },
                    content: { backgroundColor: 'var(--bg-base)', border: '1px solid var(--border-colour)' },
                }, children: _jsxs(Stack, { gap: "md", children: [_jsx(TextInput, { label: "Department Name", required: true, value: editForm.name, onChange: (e) => setEditForm({ ...editForm, name: e.target.value }), styles: {
                                label: { color: 'var(--text-primary)', marginBottom: 4 },
                                input: { backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-colour)', color: 'var(--text-primary)' },
                            } }), _jsx(NumberInput, { label: "Max Checkout Duration (hours)", min: 1, max: 168, value: editForm.maxCheckoutDurationHours, onChange: (value) => setEditForm({ ...editForm, maxCheckoutDurationHours: Number(value) || 8 }), styles: {
                                label: { color: 'var(--text-primary)', marginBottom: 4 },
                                input: { backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-colour)', color: 'var(--text-primary)' },
                            } }), _jsx(NumberInput, { label: "Stun Delay (seconds)", description: "Time after checkout expiry before radio is stunned", min: 0, max: 86400, value: editForm.stunDelaySeconds, onChange: (value) => setEditForm({ ...editForm, stunDelaySeconds: Number(value) || 300 }), styles: {
                                label: { color: 'var(--text-primary)', marginBottom: 4 },
                                input: { backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-colour)', color: 'var(--text-primary)' },
                                description: { color: 'var(--text-muted)' },
                            } }), _jsxs(Group, { justify: "flex-end", mt: "md", children: [_jsx(Button, { variant: "subtle", color: "gray", onClick: () => setEditTarget(null), children: "Cancel" }), _jsx(Button, { color: "blue", onClick: handleUpdate, loading: updateDepartment.isPending, children: "Save Changes" })] })] }) }), _jsx(Modal, { opened: !!deleteTarget, onClose: () => setDeleteTarget(null), title: "Delete Department", styles: {
                    header: { backgroundColor: 'var(--bg-card)', borderBottom: '1px solid var(--border-colour)' },
                    body: { backgroundColor: 'var(--bg-base)' },
                    title: { fontWeight: 600, color: 'var(--text-primary)' },
                    content: { backgroundColor: 'var(--bg-base)', border: '1px solid var(--border-colour)' },
                }, children: _jsxs(Stack, { gap: "md", children: [_jsxs(Text, { size: "sm", style: { color: 'var(--text-primary)' }, children: ["Are you sure you want to delete ", _jsx("strong", { children: deleteTarget?.name }), "? Departments with assigned equipment or users cannot be deleted."] }), _jsxs(Group, { justify: "flex-end", children: [_jsx(Button, { variant: "subtle", color: "gray", onClick: () => setDeleteTarget(null), children: "Cancel" }), _jsx(Button, { color: "red", onClick: handleDelete, loading: deleteDepartment.isPending, children: "Delete Department" })] })] }) }), _jsx(Modal, { opened: createModalOpen, onClose: () => setCreateModalOpen(false), title: "Add Department", styles: {
                    header: { backgroundColor: 'var(--bg-card)', borderBottom: '1px solid var(--border-colour)' },
                    body: { backgroundColor: 'var(--bg-base)' },
                    title: { fontWeight: 600, color: 'var(--text-primary)' },
                    content: { backgroundColor: 'var(--bg-base)', border: '1px solid var(--border-colour)' },
                }, children: _jsxs(Stack, { gap: "md", children: [_jsx(TextInput, { label: "Department Name", placeholder: "Operations", required: true, value: newDepartment.name, onChange: (e) => setNewDepartment({ ...newDepartment, name: e.target.value }), styles: {
                                label: { color: 'var(--text-primary)', marginBottom: 4 },
                                input: { backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-colour)', color: 'var(--text-primary)' },
                            } }), _jsx(NumberInput, { label: "Max Checkout Duration (hours)", placeholder: "8", min: 1, max: 168, value: newDepartment.maxCheckoutDurationHours, onChange: (value) => setNewDepartment({ ...newDepartment, maxCheckoutDurationHours: Number(value) || 8 }), styles: {
                                label: { color: 'var(--text-primary)', marginBottom: 4 },
                                input: { backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-colour)', color: 'var(--text-primary)' },
                            } }), _jsx(NumberInput, { label: "Stun Delay (seconds)", description: "Time after checkout expiry before radio is stunned", placeholder: "300", min: 0, max: 86400, value: newDepartment.stunDelaySeconds, onChange: (value) => setNewDepartment({ ...newDepartment, stunDelaySeconds: Number(value) || 300 }), styles: {
                                label: { color: 'var(--text-primary)', marginBottom: 4 },
                                input: { backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-colour)', color: 'var(--text-primary)' },
                                description: { color: 'var(--text-muted)' },
                            } }), _jsxs(Group, { justify: "flex-end", mt: "md", children: [_jsx(Button, { variant: "subtle", color: "gray", onClick: () => setCreateModalOpen(false), children: "Cancel" }), _jsx(Button, { color: "blue", onClick: handleCreate, loading: createDepartment.isPending, disabled: !newDepartment.name, children: "Create Department" })] })] }) })] }));
}
//# sourceMappingURL=DepartmentsPage.js.map